import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ValidatorFn } from '@angular/forms/src/directives/validators';

// core
import { CustomValidators } from '../../ng/custom-validators';
import { FlagsHelper } from '../../../core/library/flags-helper';
import { TypesHelper } from '../../library/types-helper';

// own module
import { JssNodeLeaf, JssLeafFlags } from './jss-tree-leaf';
import { JssTreeNodeType, JssControlType } from './jss-types';
import { JssAbstFlags } from './jss-tree-abstract';
import { JssEventType } from './jss-subject';
import { JssObserver, JssEventData } from './jss-observers';

export class JssNodeNumber extends JssNodeLeaf {

   protected _value = 0;
   protected _default = 0;
   protected _minimum = 0;
   protected _maximum = 0;

   constructor(
      nodeName: string,
      jssPropertyType: JssTreeNodeType,
      jssControlType: JssControlType = 'INPUT'        // default control
   ) {
      super(nodeName, jssPropertyType, jssControlType);
   }

   // Value
   public set value(value: number) {
      this._value = value;
      this._leafFlags = FlagsHelper.setFlag(
         this._leafFlags, JssLeafFlags.INITIAL_VALUE_DEFINED, true);
   }
   public get value() {return this._value; }

   // default
   public set default(value: number) {
      this._default = value;
      this._leafFlags = FlagsHelper.setFlag(
         this._leafFlags, JssLeafFlags.DEFAULT_DEFINED, true);
   }
   public get default() {return this._default; }

   // Minimum
   public set minimum(value: number) {
      this._minimum = value;
      this._leafFlags = FlagsHelper.setFlag(
         this._leafFlags, JssLeafFlags.MINIMUM_DEFINED, true);
   }
   public get minimum() {return this._minimum; }

   // Maximum
   public set maximum(value: number) {
      this._maximum = value;
      this._leafFlags = FlagsHelper.setFlag(
         this._leafFlags, JssLeafFlags.MAXIMUM_DEFINED, true);
   }
   public get maximum() {return this._maximum; }

   // value changed
   public valueChanged(value: number | string): void {
      let __val = 0;
      // convert string to interger | number
      if (typeof value === 'string') {
         if (this._nodeType === 'INTEGER' && TypesHelper.isInteger10(value)) {
            __val = parseInt(value, 10);
         } else {    // number
            __val = parseFloat(value);
         }
      } else {
         __val = value;
      }

      // prevent unneccessary updates
      if (this._value === __val) {return; }
      // store current value
      this._value = __val;
      // EventSubject registered?
      if (!this._jssEventSubject) {return; }
      this._jssEventSubject.notifyObservers('VALUE_CHANGED',
      {
         eventType: 'VALUE_CHANGED',
         control: this._nodeName,
         value: __val
      });
   }

   // event notify
   public eventNotify(eventData: JssEventData): void {
      // no rule -> return
      if (this._jssRule.length === 0) {return; }

      this._jssRule.forEach( elem => {

         // check nodeName of emitting control
         if ( eventData.control !== elem.emittingNodeName) {
            return;     // next iteration
         }

         switch (eventData.eventType) {
            case 'VALUE_CHANGED':
               // hide if expected_value is received
               if (
                  elem.effectCondition === 'EXPECTED_VALUE' &&
                  elem.effectType === 'HIDE'
               ) {
                     this._visible = !(elem.data === eventData.value);
                     // console.log('JssNodeNumber:eventNotified(HIDE):', eventData, this._visible);
               } else if (
                  elem.effectCondition === 'EXPECTED_VALUE' &&
                  elem.effectType === 'DISABLE'
               ) {
                     this._enabled = !(elem.data === eventData.value);
                     if (this._enabled) {
                        this._formControl.enable();
                     } else {
                        this._formControl.disable();
                     }
                     // console.log('JssNodeNumber:eventNotified(DISABLE):', eventData, this._enabled);
               } else {
                  console.warn('Unexpected effectCondition or effectType!',
                     elem.effectCondition,
                     elem.effectType);
               }
               break;
            default:
               console.warn('Unexpected default value detected!');
         }
      });
   }

   // Dump to console
   public recveDumpJssTree(indent: string) {
      let s = indent + this._nodeName + '<' + this._nodeType + '>=';
      if (this.hasTitle()) {
         s += 'Title:' + this._title;
      }
      if (this.isRequired()) {
         s += ' <Requ>';
      }
      if (FlagsHelper.isFlagSet(this._leafFlags, JssLeafFlags.INITIAL_VALUE_DEFINED)) {
         s += ' Value:' + this._value;
      }
      if (FlagsHelper.isFlagSet(this._leafFlags, JssLeafFlags.MAXIMUM_DEFINED)) {
         s += ' Max:' + this._maximum;
      }
      if (FlagsHelper.isFlagSet(this._leafFlags, JssLeafFlags.MINIMUM_DEFINED)) {
         s += ' Min:' + this._minimum;
      }
      if (this.hasDefault()) {
         s += ' Default:' + this._default;
      }
      s += ',lytOrder:' + this.lytOrder;
      if (this._formControl) {
         s += ',initValue:' + this._formControl.value;
      }
      console.log(s);
   }

   // recursive Funktion -> generate Controls
   public recveGenFormTree(form: FormGroup) {
      // consider pass-through flag
      if (this.valuePassThrough) {
         return;
      }

      // generate FormControl and add to parent FormGroup
      const valFn: ValidatorFn[] = [];
      let initVal = 0;

      // initial value
      if (FlagsHelper.isFlagSet(this._leafFlags, JssLeafFlags.INITIAL_VALUE_DEFINED)) {
         initVal = this._value;
      // default value
      } else if (FlagsHelper.isFlagSet(this._leafFlags, JssLeafFlags.DEFAULT_DEFINED)) {
         initVal = this._default;
      }

      // validator required
      if (this.isRequired()) {          // defined in base class
         valFn.push(Validators.required);
      }
      // validator maximum
      if (FlagsHelper.isFlagSet(this._leafFlags, JssLeafFlags.MAXIMUM_DEFINED)) {
         valFn.push(Validators.max(this._maximum));
      }
      // validator minimum
      if (FlagsHelper.isFlagSet(this._leafFlags, JssLeafFlags.MINIMUM_DEFINED)) {
         valFn.push(Validators.min(this._minimum));
      }

      if (this._nodeType === 'INTEGER') {
         valFn.push(CustomValidators.requiredInteger);
      }

      if (this._nodeType === 'NUMBER') {
         valFn.push(CustomValidators.requiredNumber);
      }

      // create control
      this._formControl = new FormControl(initVal, valFn);
      form.addControl(this._nodeName, this._formControl);
   }

   /**
   * Erzeugt ein JS Object mit dem Inhalt der Form sowie den Hidden-pass-through Daten.
   *
   * @param data: any
   * @returns void
   */
   public recveCreateDataObject(data: any) {
      // HIDDEN-pass-through und keine initialen Daten (value)
      if (!this.valuePassThrough ||
         this.valuePassThrough && FlagsHelper.isFlagSet(this._leafFlags, JssLeafFlags.INITIAL_VALUE_DEFINED)) {
         data[this.nodeName] = this._value.toString();
      }
   }

   public recveEvalSchema(dump_output: boolean) {
      // nothing to evaluate at this moment
   }
}



// WEBPACK FOOTER //
// /builds/cp/frap/t3/planer/src/app/core/json-form/model/jss-tree-number.ts